import AgencyPicker from 'app/main/backoffice/common/agency-picker/AgencyPicker';
import BasicMultiSelect from 'app/ui_v2/form-fields/basic-fields/select/BasicMultiSelect';
import BasicSelect from 'app/ui_v2/form-fields/basic-fields/select/BasicSelect';
import BasicTextInput from 'app/ui_v2/form-fields/basic-fields/text-input/TextInput';
import FormWrapper from 'app/ui_v2/form-fields/form-wrapper/FormWrapper';
import RangeDatepicker from 'app/ui_v2/form-fields/range-datepicker/RangeDatepicker';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Card, CardContent, Grid } from '@mui/material';

import {
  ALL_OPT,
  defaultValues,
  FormValues,
  GEO_STATUS_OPTIONS,
  getValidationSchema,
  PROJECT_TYPES,
  REPORT_SUBMISSION_OPTIONS,
  SEARCH_TYPES_AGENCY,
  STATUS_FILTERS,
  UNSAFE_CONDITIONS_OPTIONS,
} from './validation-schema';

export interface InspectionsSearchFormProps {
  isLoading: boolean;
  onFormSubmit: (values: FormValues) => void;
  onFormChange: (values: FormValues) => void;
  defaults?: FormValues;
  ifReset: number;
}

export default function InspectionsSearchForm(props: InspectionsSearchFormProps) {
  const { onFormSubmit, onFormChange, isLoading, defaults, ifReset } = props;

  const { handleSubmit, control, watch, reset, setValue } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { ...defaultValues, ...(defaults && { ...defaults }) },
    resolver: yupResolver(getValidationSchema()),
  });

  const [resetDatepicker, setResetDatepicker] = useState<number>(0);

  const onSubmit = (data: FormValues) => {
    onFormSubmit(data);
  };

  const handleChange = () => {
    const data = watch();

    if (data.searchBy !== '' && data.query === '') {
      return;
    }
    onFormChange(data);
  };

  const handleReset = (isSilent: boolean = false) => {
    reset(defaultValues);
    setValue('dateTo', null);
    setValue('dateFrom', null);
    setValue('finalizedAtFrom', null);
    setValue('finalizedAtTo', null);
    setResetDatepicker((v) => v + 1);
    if (!isSilent) {
      setTimeout(() => {
        handleChange();
      }, 1);
    }
  };

  useEffect(() => {
    if (ifReset > 0) {
      handleReset(true);
    }
  }, [ifReset]);

  return (
    <Card>
      <CardContent>
        <FormWrapper<FormValues>
          onChangeCallback={handleChange}
          onSubmitCallback={onSubmit}
          handleSubmit={handleSubmit}
        >
          <>
            <Grid container columnSpacing={4} spacing={4}>
              <Grid item xs={12} sm={12} md={4} lg={3}>
                <BasicSelect
                  collection={SEARCH_TYPES_AGENCY}
                  label="Search By"
                  inputName="searchBy"
                  control={control}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={4} lg={3}>
                <BasicTextInput label="Search" inputName="query" control={control} />
              </Grid>

              <Grid item xs={12} sm={12} md={4} lg={3}>
                <AgencyPicker control={control} fieldName="agencyCompanyId" fieldLabel="Agency" />
              </Grid>

              <Grid item xs={12} sm={12} md={4} lg={3}>
                <BasicSelect
                  collection={[ALL_OPT, ...GEO_STATUS_OPTIONS]}
                  label="GEO status"
                  inputName="geolocationStatus"
                  control={control}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={8} lg={6}>
                <RangeDatepicker
                  control={control}
                  reset={resetDatepicker}
                  config={{
                    dateFromLabel: 'Insp. start from',
                    dateToLabel: 'Insp. start to',
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={8} lg={6}>
                <RangeDatepicker
                  control={control}
                  reset={resetDatepicker}
                  config={{
                    dateFromLabel: 'Insp. finalized from',
                    dateToLabel: 'Insp. finalized to',
                  }}
                  inputFromName="finalizedAtFrom"
                  inputToName="finalizedAtTo"
                />
              </Grid>

              <Grid item xs={12} sm={12} md={4} lg={3}>
                <BasicSelect
                  collection={[ALL_OPT, ...UNSAFE_CONDITIONS_OPTIONS]}
                  label="Unsafe cond."
                  inputName="unsafeConditions"
                  control={control}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={4} lg={3}>
                <BasicSelect
                  collection={[ALL_OPT, ...REPORT_SUBMISSION_OPTIONS]}
                  label="Report submission"
                  inputName="submissionTimeMet"
                  control={control}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={3}>
                <BasicMultiSelect
                  collection={STATUS_FILTERS}
                  label="Status"
                  inputName="states"
                  control={control}
                  isNested={true}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={3}>
                <BasicSelect
                  collection={[ALL_OPT, ...PROJECT_TYPES]}
                  label="Project Type"
                  inputName="jobType"
                  control={control}
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" mt={2.5}>
              <Button
                type="button"
                variant="outlined"
                color="primary"
                disabled={isLoading}
                disableElevation
                onClick={() => {
                  handleReset();
                }}
                sx={{
                  width: {
                    xs: '100%',
                    sm: 'auto',
                    md: 'auto',
                    lg: 'auto',
                  },
                }}
              >
                Reset
              </Button>
            </Box>
          </>
        </FormWrapper>
      </CardContent>
    </Card>
  );
}
